import styles from "../forms.module.css";
import { ITreatmentGroup, ITherapyType, ITreatmentGroupMember } from "../../../services/types/utils.types";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import CloseButton from "../../closeButton/closeButton";
import { IUserReport } from "../../../services/types/users.types";
import GroupMembersList from "../../groupMembersList/groupMembersList";
import { useAppSelector } from "../../../services/hooks";
import SelectContactPopup from "../../modals/selectContactPopup/selectContactPopup";
import DeleteConfirmation from "../../modals/deleteConfirmation/deleteConfirmation";

import Modal from "../../modals/modal/Modal";
import GeneralSuccessPopup from "../../modals/generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../../modals/errorPopup/errorPopup";
import Select, { ActionMeta, SingleValue } from "react-select";
import { getStatusOptions } from "../../../utils/constants";
import { TLabeledItem } from "../../../types/types";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import { adminSelector } from "../../../services/selectors/user.selectors";
import { useCreateTreatmentGroupMutation, useUpdateTreatmentGroupMutation, useGetTherapyTypesQuery, useAddGroupMemberMutation, useRemoveGroupMemberMutation, useGetTreatmentGroupByIdQuery } from "../../../services/api/utils.api";
import { useLazyGetUsersByCompanyIdWithRecommendsQuery } from "../../../services/api/user.api";

interface IAddOrEditTreatmentGroupFormProps {
  form: ITreatmentGroup;
  handleChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onClose: () => void;
  handleSelectChange: (
    newValue: SingleValue<TLabeledItem>,
    actionMeta: ActionMeta<TLabeledItem>
  ) => void;
}

function AddOrEditTreatmentGroupForm({
  form,
  handleChange,
  onClose,
  handleSelectChange,
}: IAddOrEditTreatmentGroupFormProps) {
  const { data: therapyTypesData } = useGetTherapyTypesQuery();
  const admin = useAppSelector(adminSelector);
  const [createGroup] = useCreateTreatmentGroupMutation();
  const [updateGroup] = useUpdateTreatmentGroupMutation();
  const [getUsers, { data: usersData }] = useLazyGetUsersByCompanyIdWithRecommendsQuery();
  const [addGroupMember] = useAddGroupMemberMutation();
  const [removeGroupMember] = useRemoveGroupMemberMutation();

  const isEditMode = !!form.id;
  const { data: groupData } = useGetTreatmentGroupByIdQuery(
    { id: form.id || 0 },
    { skip: !isEditMode }
  );

  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [showAddMemberPopup, setShowAddMemberPopup] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<ITreatmentGroupMember | null>(null);
  const [touched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUnlimited, setIsUnlimited] = useState(
    form.maxParticipants === undefined || form.maxParticipants === null
  );
  const [currentMembers, setCurrentMembers] = useState<ITreatmentGroupMember[]>([]);

  // Sync isUnlimited state when form prop changes (e.g., when editing existing group)
  useEffect(() => {
    setIsUnlimited(form.maxParticipants === undefined || form.maxParticipants === null);
  }, [form.maxParticipants]);

  useEffect(() => {
    if (groupData?.members) {
      setCurrentMembers(groupData.members);
    }
    else{
      setCurrentMembers([]);
    }
  }, [groupData, form.members]);

  const statusOptions = getStatusOptions();

  const handleAddMemberClick = () => {
    getUsers({ companyIds: [admin.companyId], roleIds: [1] });
    setShowAddMemberPopup(true);
  };

  const handleSelectMember = async (contact: IUserReport) => {
    try {
      await addGroupMember({
        treatmentGroupId: form.id,
        userId: contact.Users.Id,
      }).unwrap();
    } catch (error) {
      console.error("Error adding member:", error);
      setOpenModalError(true);
    }
  };

  const handleRemoveMember = (member: ITreatmentGroupMember) => {
    setMemberToDelete(member);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      await removeGroupMember({
        groupId: form.id,
        userId: memberToDelete.userId,
      }).unwrap();
      setShowDeleteConfirmation(false);
      setMemberToDelete(null);
    } catch (error) {
      console.error("Error removing member:", error);
      setOpenModalError(true);
    }
  };


  const availableContacts = useMemo(() => {
    if (!usersData?.Users) return [];
    const memberIds = new Set(currentMembers.map((m) => m.userId));
    return usersData.Users.filter((u) => !memberIds.has(u.Users.Id));
  }, [usersData, currentMembers]);

  const onInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setTouched(true);
    handleChange(e);
  };

  const onSelectChange = (
    newValue: SingleValue<TLabeledItem>,
    actionMeta: ActionMeta<TLabeledItem>
  ) => {
    setTouched(true);
    handleSelectChange(newValue, actionMeta);
  };

  const onUnlimitedChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTouched(true);
    setIsUnlimited(e.target.checked);
    if (e.target.checked) {
      handleChange({
        target: { name: "maxParticipants", value: undefined },
      } as any);
    }
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);

      if (isEditMode) {
        // Update existing group
        await updateGroup({
          id: form.id,
          groupName: form.groupName,
          maxParticipants: isUnlimited ? undefined : form.maxParticipants,
          therapyTypeId: form.therapyTypeId,
          therapistId: form.therapistId,
        }).unwrap();
      } else {
        // Create new group
        await createGroup({
          groupName: form.groupName,
          maxParticipants: isUnlimited ? undefined : form.maxParticipants,
          therapyTypeId: form.therapyTypeId,
          therapistId: form.therapistId,
          companyId: admin.companyId,
        }).unwrap();
      }
      setOpenModalSuccess(true);
    } catch (error) {
      console.error("Error saving treatment group:", error);
      setOpenModalError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const therapyTypeOptions: TLabeledItem[] = useMemo(() => {
    const list: ITherapyType[] = (therapyTypesData ?? []).map((t: any) => ({
      id: t.Id ?? t.id,
      name: t.Name ?? t.name,
    }));
    return list.map((type: ITherapyType) => ({
      value: type.id.toString(),
      label: type.name,
    }));
  }, [therapyTypesData]);

  return (
    <form className={styles.form}>
      <CloseButton onClick={onClose} />
      <h1 className="title title_blue">
        {isEditMode ? t('EDIT_TREATMENT_GROUP') : t('ADD_TREATMENT_GROUP')}
      </h1>

      <ul className={`${styles.form__list} ${styles.form__list_type_fourColumns}`}>
        <li>
          <label htmlFor="groupName" className={styles.form__label}>
            {t('GROUP_NAME')}
          </label>
          <input
            type="text"
            className={`${styles.form__input}`}
            name="groupName"
            id="groupName"
            value={form.groupName || ""}
            onChange={onInputChange}
            placeholder={t('ENTER_GROUP_NAME')}
          />
        </li>

        <li>
          <label htmlFor="maxParticipants" className={styles.form__label}>
            {t('MAX_PARTICIPANTS')}
          </label>
          <input
            type="number"
            className={`${styles.form__input}`}
            name="maxParticipants"
            id="maxParticipants"
            value={isUnlimited ? "" : (form.maxParticipants || "")}
            onChange={onInputChange}
            min="1"
            max="100"
            disabled={isUnlimited}
            placeholder={t('ENTER_MAX_PARTICIPANTS')}
          />
          <div style={{ marginTop: "8px" }}>
            <input
              type="checkbox"
              id="unlimited"
              checked={isUnlimited}
              onChange={onUnlimitedChange}
            />
            <label
              htmlFor="unlimited"
              className={styles.form__label}
              style={{ marginLeft: "8px", display: "inline" }}
            >
              {t('UNLIMITED')}
            </label>
          </div>
        </li>

        <li>
          <label htmlFor="therapyTypeId" className={styles.form__label}>
            {t('TREATMENT_TYPE')}
          </label>
          <Select
            name="therapyTypeId"
            options={therapyTypeOptions}
            value={therapyTypeOptions.find(
              (opt) => opt.value === form.therapyTypeId?.toString()
            )}
            placeholder={t('SELECT_TREATMENT_TYPE')}
            className={styles.select}
            onChange={onSelectChange}
          />
        </li>

        <li>
          <label htmlFor="statusId" className={styles.form__label}>
            {t("COLUMN_STATUS")}
          </label>
          <Select
            name="statusId"
            options={statusOptions}
            value={statusOptions.find(
              (opt) => opt.value === form.statusId?.toString()
            )}
            placeholder={t("STATUS_PLACEHOLDER")}
            className={styles.select}
            onChange={onSelectChange}
          />
        </li>
      </ul>

      {/* Members Section - Show in edit mode */}
      {isEditMode && (
        <div className={styles.membersSection}>
          <div
            className={styles.membersHeader}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "55px 10px 10px ",
            }}
          >
            <h3 className="title title_small">{t("GROUP_MEMBERS")}</h3>
            <button
              type="button"
              className={`button button_small ${styles.form__smallButton}`}
              onClick={handleAddMemberClick}
              style={{ width: "auto", padding: "0 15px" }}
            >
              {t("ADD_USER_TO_GROUP")}
            </button>
          </div>
          {currentMembers.length > 0 ? (
            <GroupMembersList
              members={currentMembers}
              title=""
              maxParticipants={form.maxParticipants}
              onRemoveMember={handleRemoveMember}
            />
          ) : (
            <p>{t("NO_MEMBERS_IN_GROUP")}</p>
          )}
        </div>
      )}      <div className={styles.form__buttons}>
        <button
          type="button"
          className={`button ${styles.form__btn} ${(isLoading || !form.groupName || (isEditMode && !touched)) &&
            styles.form__btn_disabled
            }`}
          disabled={isLoading || !form.groupName || (isEditMode && !touched)}
          onClick={onSubmit}
        >
          {isLoading
            ? t("SENDING")
            : isEditMode
              ? t('EDIT_TREATMENT_GROUP')
              : t('CREATE_TREATMENT_GROUP')
          }
        </button>
      </div>

      {openModalSuccess && (
        <Modal
          onClose={() => {
            setOpenModalSuccess(false);
            onClose();
          }}
        >
          <GeneralSuccessPopup
            onClose={() => {
              setOpenModalSuccess(false);
              onClose();
            }}
          />
        </Modal>
      )}

      {openModalError && (
        <Modal onClose={() => setOpenModalError(false)}>
          <ErrorPopup onClose={() => setOpenModalError(false)} />
        </Modal>
      )}

      {showAddMemberPopup && (
        <Modal onClose={() => setShowAddMemberPopup(false)} wide>
          <SelectContactPopup
            contacts={availableContacts}
            onConfirm={(selected) => {
              if (selected.length > 0) handleSelectMember(selected[0]);
            }}
            onClose={() => setShowAddMemberPopup(false)}
          />
        </Modal>
      )}

      {showDeleteConfirmation && (
        <Modal onClose={() => setShowDeleteConfirmation(false)}>
          <DeleteConfirmation
            onClose={() => setShowDeleteConfirmation(false)}
            onDelete={confirmDeleteMember}
            text={t("ARE_YOU_SURE_DELETE_MEMBER")}
          />
        </Modal>
      )}
    </form>
  );
}

export default AddOrEditTreatmentGroupForm;
